import type {
  FlowEditorConfigDraft,
  FlowEditorDocumentOp,
  FlowEditorStepDraft,
  FlowEditorTemplateDocument,
} from './types'

/**
 * Apply a single edit operation to the canonical draft document, returning a
 * NEW document. This replaces the per-keystroke `buildDocumentFromEditorState`
 * rebuild: instead of regenerating the whole template, each op copies only the
 * parts it touches (structural sharing) and re-derives contiguous `order` for
 * the affected group / config list. `serverId` identity is always preserved so
 * the save/preview diff continues to pair baseline and draft correctly.
 */
export function applyDocumentOp(
  document: FlowEditorTemplateDocument,
  op: FlowEditorDocumentOp,
): FlowEditorTemplateDocument {
  switch (op.kind) {
    case 'setTemplateName':
      return { ...document, name: op.name }

    case 'setGroupSettings': {
      const config = document.configsByLocalId[op.localId]

      if (!config) {
        return document
      }

      return replaceConfig(document, { ...config, ...op.patch })
    }

    case 'setStepScalars': {
      const step = document.stepsByLocalId[op.localId]

      if (!step) {
        return document
      }

      return replaceStep(document, { ...step, ...op.patch })
    }

    case 'setStepConfig': {
      const step = document.stepsByLocalId[op.localId]

      if (!step) {
        return document
      }

      return replaceStep(document, { ...step, config: op.config })
    }

    case 'setStepLabel': {
      const step = document.stepsByLocalId[op.localId]

      if (!step) {
        return document
      }

      return replaceStep(document, { ...step, collectDataTemplateStepLabel: op.label })
    }

    case 'addGroup': {
      if (document.configsByLocalId[op.localId]) {
        return document
      }

      const newConfig: FlowEditorConfigDraft = {
        localId: op.localId,
        serverId: undefined,
        label: op.label ?? 'Step group',
        delayed: op.delayed ?? false,
        title: null,
        order: document.configLocalIds.length,
        insurancePolicyId: op.insurancePolicyId,
        stepLocalIds: [],
      }

      return {
        ...document,
        configLocalIds: [...document.configLocalIds, op.localId],
        configsByLocalId: { ...document.configsByLocalId, [op.localId]: newConfig },
      }
    }

    case 'removeGroup': {
      const config = document.configsByLocalId[op.localId]

      if (!config) {
        return document
      }

      const configsByLocalId = { ...document.configsByLocalId }
      delete configsByLocalId[op.localId]

      const stepsByLocalId = { ...document.stepsByLocalId }
      for (const stepLocalId of config.stepLocalIds) {
        delete stepsByLocalId[stepLocalId]
      }

      const next: FlowEditorTemplateDocument = {
        ...document,
        configLocalIds: document.configLocalIds.filter((configLocalId) => {
          return configLocalId !== op.localId
        }),
        configsByLocalId,
        stepsByLocalId,
      }

      return reindexConfigs(next)
    }

    case 'reorderGroup': {
      if (!document.configsByLocalId[op.localId]) {
        return document
      }

      const withoutId = document.configLocalIds.filter((configLocalId) => {
        return configLocalId !== op.localId
      })
      const clampedIndex = clamp(op.toIndex, 0, withoutId.length)
      const configLocalIds = [
        ...withoutId.slice(0, clampedIndex),
        op.localId,
        ...withoutId.slice(clampedIndex),
      ]

      return reindexConfigs({ ...document, configLocalIds })
    }

    case 'addSteps': {
      const config = document.configsByLocalId[op.configLocalId]

      if (!config || op.steps.length === 0) {
        return document
      }

      const stepsByLocalId = { ...document.stepsByLocalId }
      const addedIds: string[] = []

      for (const step of op.steps) {
        const placed: FlowEditorStepDraft = { ...step, configLocalId: op.configLocalId }
        stepsByLocalId[placed.localId] = placed
        addedIds.push(placed.localId)
      }

      const nextConfig: FlowEditorConfigDraft = {
        ...config,
        stepLocalIds: [...config.stepLocalIds, ...addedIds],
      }

      return reindexGroup(
        { ...replaceConfig(document, nextConfig), stepsByLocalId },
        op.configLocalId,
      )
    }

    case 'removeStep': {
      const step = document.stepsByLocalId[op.localId]

      if (!step) {
        return document
      }

      const config = document.configsByLocalId[step.configLocalId]
      const stepsByLocalId = { ...document.stepsByLocalId }
      delete stepsByLocalId[op.localId]

      let next: FlowEditorTemplateDocument = { ...document, stepsByLocalId }

      if (config) {
        next = replaceConfig(next, {
          ...config,
          stepLocalIds: config.stepLocalIds.filter((stepLocalId) => {
            return stepLocalId !== op.localId
          }),
        })

        return reindexGroup(next, config.localId)
      }

      return next
    }

    case 'moveStep': {
      const step = document.stepsByLocalId[op.localId]
      const toConfig = document.configsByLocalId[op.toConfigLocalId]

      if (!step || !toConfig) {
        return document
      }

      const fromConfigLocalId = step.configLocalId
      const fromConfig = document.configsByLocalId[fromConfigLocalId]

      // Remove from source group.
      const configsByLocalId = { ...document.configsByLocalId }

      if (fromConfig) {
        configsByLocalId[fromConfigLocalId] = {
          ...fromConfig,
          stepLocalIds: fromConfig.stepLocalIds.filter((stepLocalId) => {
            return stepLocalId !== op.localId
          }),
        }
      }

      // Insert into destination group at the requested index.
      const destBase = configsByLocalId[op.toConfigLocalId]
      const destIds = destBase.stepLocalIds.filter((stepLocalId) => {
        return stepLocalId !== op.localId
      })
      const clampedIndex = clamp(op.toIndex, 0, destIds.length)
      configsByLocalId[op.toConfigLocalId] = {
        ...destBase,
        stepLocalIds: [
          ...destIds.slice(0, clampedIndex),
          op.localId,
          ...destIds.slice(clampedIndex),
        ],
      }

      const stepsByLocalId = {
        ...document.stepsByLocalId,
        [op.localId]: { ...step, configLocalId: op.toConfigLocalId },
      }

      let next: FlowEditorTemplateDocument = { ...document, configsByLocalId, stepsByLocalId }
      next = reindexGroup(next, op.toConfigLocalId)

      if (fromConfigLocalId !== op.toConfigLocalId) {
        next = reindexGroup(next, fromConfigLocalId)
      }

      return next
    }

    case 'replaceGroupSteps': {
      const config = document.configsByLocalId[op.configLocalId]

      if (!config) {
        return document
      }

      const stepsByLocalId = { ...document.stepsByLocalId }
      for (const stepLocalId of config.stepLocalIds) {
        delete stepsByLocalId[stepLocalId]
      }

      const newIds: string[] = []
      for (const step of op.steps) {
        const placed: FlowEditorStepDraft = { ...step, configLocalId: op.configLocalId }
        stepsByLocalId[placed.localId] = placed
        newIds.push(placed.localId)
      }

      const nextConfig: FlowEditorConfigDraft = { ...config, stepLocalIds: newIds }

      return reindexGroup(
        { ...replaceConfig(document, nextConfig), stepsByLocalId },
        op.configLocalId,
      )
    }

    case 'recreateGroupWithSteps': {
      const oldIndex = document.configLocalIds.indexOf(op.oldLocalId)

      if (oldIndex < 0) {
        return document
      }

      const oldConfig = document.configsByLocalId[op.oldLocalId]
      const stepsByLocalId = { ...document.stepsByLocalId }

      if (oldConfig) {
        for (const stepLocalId of oldConfig.stepLocalIds) {
          delete stepsByLocalId[stepLocalId]
        }
      }

      const configsByLocalId = { ...document.configsByLocalId }
      delete configsByLocalId[op.oldLocalId]

      const placedSteps = op.steps.map((step) => {
        return { ...step, configLocalId: op.newLocalId }
      })
      for (const step of placedSteps) {
        stepsByLocalId[step.localId] = step
      }

      configsByLocalId[op.newLocalId] = {
        localId: op.newLocalId,
        serverId: undefined,
        label: op.label,
        delayed: op.delayed,
        title: null,
        order: oldIndex,
        insurancePolicyId: op.insurancePolicyId,
        stepLocalIds: placedSteps.map((step) => {
          return step.localId
        }),
      }

      const configLocalIds = [...document.configLocalIds]
      configLocalIds.splice(oldIndex, 1, op.newLocalId)

      const next = reindexConfigs({
        ...document,
        configLocalIds,
        configsByLocalId,
        stepsByLocalId,
      })

      return reindexGroup(next, op.newLocalId)
    }

    default:
      return assertNever(op)
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function replaceConfig(
  document: FlowEditorTemplateDocument,
  config: FlowEditorConfigDraft,
): FlowEditorTemplateDocument {
  return {
    ...document,
    configsByLocalId: { ...document.configsByLocalId, [config.localId]: config },
  }
}

function replaceStep(
  document: FlowEditorTemplateDocument,
  step: FlowEditorStepDraft,
): FlowEditorTemplateDocument {
  return {
    ...document,
    stepsByLocalId: { ...document.stepsByLocalId, [step.localId]: step },
  }
}

/** Re-derive each config's `order` from its position in `configLocalIds`. */
function reindexConfigs(document: FlowEditorTemplateDocument): FlowEditorTemplateDocument {
  const configsByLocalId = { ...document.configsByLocalId }

  document.configLocalIds.forEach((localId, index) => {
    const config = configsByLocalId[localId]

    if (config && config.order !== index) {
      configsByLocalId[localId] = { ...config, order: index }
    }
  })

  return { ...document, configsByLocalId }
}

/** Re-derive each step's `order` from its position in the group's `stepLocalIds`. */
function reindexGroup(
  document: FlowEditorTemplateDocument,
  configLocalId: string,
): FlowEditorTemplateDocument {
  const config = document.configsByLocalId[configLocalId]

  if (!config) {
    return document
  }

  const stepsByLocalId = { ...document.stepsByLocalId }
  let changed = false

  config.stepLocalIds.forEach((stepLocalId, index) => {
    const step = stepsByLocalId[stepLocalId]

    if (step && step.order !== index) {
      stepsByLocalId[stepLocalId] = { ...step, order: index }
      changed = true
    }
  })

  return changed ? { ...document, stepsByLocalId } : document
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function assertNever(value: never): never {
  throw new Error(`Unhandled document op: ${JSON.stringify(value)}`)
}
